import { Box, Divider, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowLeft,
  ArrowRight, BookOpen,
  Brain,
  Building2,
  Earth, Globe, GraduationCap, Handshake,
  Leaf, MessageCircleMore,
  Network,
  Shield,
  Smartphone,
  TrendingUp, User, Users
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ArchitectureCard, ArchitectureCardArrowSeparator,
  ArchitectureCardIconContainer, ArchitectureListContainer,
  Container,
  DescriptionPlayerContainer, FacultyCard,
  FacultyCardContent, FacultyCardOverlay, GovernanceCard, GovernanceCardIndexContainer, GovernanceListContainer,
  Header, ValueCard, ValuesContainer
} from "./Academy.styles";
import { Modal, Spinner } from "../../components";
import AcademyPlayer from "../../components/AcademyPlayer/AcademyPlayer.component";
import { Button } from "../../components/Button/Button.component";
import { LEVEL } from "../../consts";
import { useStartOrResumeQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";

const governanceItems = [
  { title: "Governance", description: "Strategia e allocazione del budget" },
  { title: "Comitato Scientifico", description: "Macro-contenuti e monitoraggio" },
  { title: "Comitato Tecnico", description: "Progettazione e delivery" }
];

const facultiesList = [
  { title: "Cultura Manageriale", description: "Sviluppa competenze di leadership e gestione strategica per guidare team e organizzazioni verso il successo.", icon: Building2, background: "https://static.openlearning-test.digitedacademy.net/media/cultura manageriale.png" },
  { title: "AI & Data", description: "Esplora il modulo dell'intelligenza artificiale e della scienza dei dati per trasformare i dati in insights strategici.", icon: Brain, background: "https://images.unsplash.com/photo-1697577418970-95d99b5a55cf" },
  { title: "Sviluppo reti", description: "Costruisci e gestisci reti di business efficaci per l'espansione commerciale e l'ottimizzazione delle performance.", icon: Network, background: "https://images.unsplash.com/photo-1758304480414-a78219c4e121" },
  { title: "Wealth", description: "Specializzati nella gestione  petrimoniale e nella consulenza finanziaria per clienti privati e istituzionali.", icon: TrendingUp, background: "https://images.unsplash.com/photo-1705948734578-54b4e4dbf443" },
  { title: "ESG", description: "Approfondisci i principi della sostenibilità ambientale, sociale e di governance per strategie aziendali responsabili.", icon: Leaf, background: "https://images.unsplash.com/photo-1607369165516-0e831913b397" },
  { title: "Risk culture", description: "Sviluppa una solida cultura del rischio per identificare, valutare e gestire efficacemente i rischi aziendali.", icon: Shield, background: "https://images.unsplash.com/photo-1644093387522-b1be6de270a5" },
  { title: "Internazionalizzazione", description: "Preparati per il mercato globale con competenze linguistiche e culturali per operare in contesti internazionali.", icon: Earth, background: "https://static.openlearning-test.digitedacademy.net/media/internazionalizzazione.png" },
  { title: "Digital Innovation", description: "Abbraccia la trasformazione digitale e impara a guidare l'innovazione tecnologica nella tua organizzazione.", icon: Smartphone, background: "https://images.unsplash.com/photo-1758524572081-5a84e02768ef" },
  { title: "Customer Experience", description: "Eccelli nella gestione dell'esperienza cliente creando relazioni durature e valore per il business.", icon: Users, background: "https://images.unsplash.com/photo-1758524572081-5a84e02768ef" }
];

const valuesList = [
  { title: "\"Back to class\"", description: "Recupero della dimensione formativa in presenza, sempre integrata da formati blended a distanza e digitali", icon: GraduationCap },
  { title: "Personalizzazione", description: "Programmi dedicati a tutte le Persone del Gruppo e altri rivolti a target più specifici, per un consolidamento delle competenze necessarie", icon: User },
  { title: "Dialogo", description: "Per co-disegnare e co-progettare i nostri contenuti insieme a tutte le Divisioni e Aree di Governo del Gruppo", icon: MessageCircleMore },
  { title: "Partnership", description: "Partnership di eccellenza con il mondo accademico e della consulenza per portare alle nostre Persone i contenuti più efficaci", icon: Handshake },
  { title: "Internazionalizzazione", description: "Un’Academy che arrivi anche alle Persone che lavorano nelle strutture internazionali, e che acceleri il processo di internazionalizzazione intrapreso dal Gruppo", icon: Globe }
];

const architectureList = [
  { title: "Academy", description: "La struttura principale che coordina l'intera offerta formativa", icon: Building2 },
  { title: "Faculties", description: "9 aree specializzate, ognuna con un comitato dedicato", icon: Users },
  { title: "Programmi", description: "Percorsi specifici definiti da comitati delle faculties", icon: BookOpen }
];

const isNotFoundCode = "core-005";

export function Academy() {

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const homeLink = {
    label: smartConfiguration?.visForyou === false ? "Esplora" : "Per Te",
    path: smartConfiguration?.visForyou === false ? "/esplora" : "/per-te"
  };

  const { id: idString } = useParams<{
    id: string
  }>();

  const idNumber = idString ? Number(idString) : NaN;
  const language = useLanguageStore(state => state.language)?.id;

  const {
    data: learningObject,
    error:learningObjectError,
    isLoading: learningObjectIsFetching,
    isError:learningObjectIsError
  } = useStartOrResumeQuery({
    language,
    learningObjectId: idNumber,
    learningObjectTypology: LEVEL.DA_URL_RES
  });

  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== LEVEL.DA_URL_RES;
  }, [learningObjectIsFetching, learningObject]);

  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";

  if(isNotFound || hasWrongType){
    navigate("/not-found");
  }

  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }

  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  if(learningObjectIsFetching) return (
    <Box sx={ {
      paddingTop: "32px",
      paddingBottom: "32px"
    } }>
      <Spinner />
    </Box>
  );

  return (
    <>
      <Header theme={ theme }>
        <Container $isMobile={ isMobile }>
          <Button onClick={ () => navigate(homeLink.path) } startIcon={ <ArrowLeft color={ theme.customColors.systemSecondary01 }/> } variant={ "text" }>
            <Typography color={ theme.customColors.systemSecondary01 }>Indietro</Typography>
          </Button>
        </Container>
      </Header>

      { /*HeroPlayer*/ }
      <AcademyPlayer url={ "https://static.openlearning-test.digitedacademy.net/media/academy2.mp4" } height={ isMobile ? "380px" : "710px" }/>

      { /*Descrizione*/ }
      <Container $isMobile={ isMobile }>
        <Box sx={ {
          paddingY: "95px"
        } }>
          <Stack direction={ isMobile ? "column" : "row" } alignItems={ "center" } gap={ "48px" }>
            <Stack direction={ "column" } gap={ "24px" } sx={ {
              width: isMobile ? "100%" : "40%",
              textAlign: isMobile ? "center" : "left"
            } }>
              <Typography fontSize={ 28 } fontWeight={ 700 }>{ "La Corporate Academy di Gruppo" }</Typography>
              <Typography fontSize={ 16 }>{ "Si chiama Academy4Future e nasce con l’obiettivo di investire sul futuro delle nostre Persone con percorsi formativi pensati per motivarle e farle crescere, sviluppando le competenze strategiche necessarie per l’evoluzione del nostro Gruppo." }</Typography>
              <Typography fontSize={ 16 }>{ "La Corporate Academy ridefinisce il modo di imparare, combinando il ritorno alla formazione in presenza con un’offerta digitale sempre più ampia." }</Typography>
            </Stack>

            <DescriptionPlayerContainer $isMobile={ isMobile }>
              <AcademyPlayer url={ "https://static.openlearning-test.digitedacademy.net/media/academy2.mp4" }/>
            </DescriptionPlayerContainer>
          </Stack>
        </Box>
      </Container>

      { /*Architettura*/ }
      <Box sx={ {
        backgroundColor: theme.customColors.backgroundPrimaryLighter
      } }>
        <Container $isMobile={ isMobile }>
          <Box sx={ {
            paddingY: "95px"
          } }>
            <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" } sx={ {
                textAlign: isMobile ? "center" : "left"
              } }>
                <Typography fontSize={ 28 } fontWeight={ 700 }>{ "L'Architettura dell'Academy" }</Typography>
                <Typography component={ "h4" } fontSize={ 16 }>{ "Un sistema organizzato e strutturato per garantire formazione di eccellenza" }</Typography>
              </Stack>

              <ArchitectureListContainer theme={ theme }>
                { architectureList.map((architecture, index) => {

                  const isFirst = (index === 0);
                  const Icon = architecture.icon;

                  return (
                    <Fragment key={ `architecture_card_${index}` }>
                      <ArchitectureCard theme={ theme } $isFirst={ isFirst }>
                        <ArchitectureCardIconContainer theme={ theme }>
                          <Icon size={ 48 } color={ isFirst ? theme.customColors.textPrimary : theme.customColors.backgroundPrimaryCta } />
                        </ArchitectureCardIconContainer>
                        <Typography component={ "h4" } textAlign={ "center" } fontSize={ 16 } fontWeight={ 600 } sx={ { color: !isFirst ? theme.customColors.textPrimary:undefined } }>{ architecture.title }</Typography>
                        <Typography textAlign={ "center" } fontSize={ 14 } fontWeight={ 400 } sx={ { color: !isFirst ? theme.customColors.textPrimary:undefined } } dangerouslySetInnerHTML={ { __html: architecture.description } }/>
                      </ArchitectureCard>

                      { (index < architectureList.length -1) && (
                        <ArchitectureCardArrowSeparator theme={ theme }>
                          <ArrowRight size={ 48 } color={ theme.customColors.backgroundPrimaryCta }/>
                        </ArchitectureCardArrowSeparator>
                      ) }
                    </Fragment>
                  );
                }) }
              </ArchitectureListContainer>

              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "16px" } width={ "100%" }>
                <Typography fontSize={ 28 } fontWeight={ 700 }>{ "Governance" }</Typography>
                <Divider sx={ {
                  width: "100%"
                } }/>
                <GovernanceListContainer theme={ theme }>
                  { governanceItems.map((item, index) => (
                    <GovernanceCard key={ `governance_item_${index}` } theme={ theme }>
                      <Stack direction={ "row" } alignItems={ "center" } gap={ "12px" }>
                        <GovernanceCardIndexContainer theme={ theme }>
                          <Typography>{ index + 1 }</Typography>
                        </GovernanceCardIndexContainer>
                        <Typography component={ "h4" } fontSize={ 16 } fontWeight={ 700 }>{ item.title }</Typography>
                      </Stack>
                      <Typography fontSize={ 13 }>{ item.description }</Typography>
                    </GovernanceCard>
                  )) }
                </GovernanceListContainer>
              </Stack>
            </Stack>
          </Box>
        </Container>
      </Box>

      { /*Faculties*/ }
      <Container $isMobile={ isMobile }>
        <Box sx={ {
          paddingY: "95px"
        } }>
          <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
            <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" }>
              <Typography fontSize={ 28 } fontWeight={ 700 }>{ "Le Nostre Faculty" }</Typography>
              <Typography component={ "h4" } fontSize={ 16 } fontWeight={ 700 }>{ "Esplora le 9 aree di specializzazione che compongono l'Academy 4 Future." }</Typography>
            </Stack>

            <Grid container spacing={ "32px" }>
              { facultiesList.map((faculty, index) => {
                const Icon = faculty.icon;
                return (
                  <Grid key={ `faculty_${index}` } item xs={ 12 } sm={ 6 } md={ 4 }>
                    <FacultyCard theme={ theme }>
                      <img src={ faculty.background } alt={ faculty.title } style={ { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" } } loading="lazy"/>
                      <FacultyCardOverlay theme={ theme }/>
                      <FacultyCardContent theme={ theme }>
                        <Stack direction={ "column" } justifyContent={ "end" } alignItems={ "start" } gap={ "13px" } height={ "100%" } padding={ "28px 24px" }>
                          <Icon size={ 32 } />
                          <Typography variant={ "h3" } fontSize={ 16 } fontWeight={ 700 }>{ faculty.title }</Typography>
                          <Typography fontSize={ 14 } dangerouslySetInnerHTML={ { __html: faculty.description } }/>
                        </Stack>
                      </FacultyCardContent>
                    </FacultyCard>
                  </Grid>
                );
              }) }
              { /* card da poter mostrare se ci sono elementi incoming
              <Grid item xs={ 12 } sm={ 6 } md={ 4 }>
                <FacultyCard theme={ theme }>
                  <img src={ "https://images.unsplash.com/photo-1712331393873-f671bec94e21" } alt={ "Coming soon" } style={ { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" } } loading="lazy"/>
                  <FacultyCardOverlay theme={ theme }/>
                  <FacultyCardContent theme={ theme }>
                    <Typography variant={ "h3" } fontSize={ 22 } fontWeight={ 700 }>{ "Coming Soon" }</Typography>
                  </FacultyCardContent>
                </FacultyCard>
              </Grid>
             */ }
            </Grid>
          </Stack>
        </Box>
      </Container>

      { /*DNA*/ }
      <Box sx={ {
        backgroundColor: theme.customColors.backgroundFooter
      } }>
        <Container $isMobile={ isMobile }>
          <Box sx={ {
            paddingY: "95px"
          } }>
            <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" } sx={ {
                textAlign: isMobile ? "center" : "left"
              } }>
                <Typography fontSize={ 28 } fontWeight={ 700 }>{ "Il DNA della nostra Academy" }</Typography>
                <Typography component={ "h4" } fontSize={ 16 } fontWeight={ 600 }>{ "I valori fondamentali che guidano ogni aspetto della nostra formazione e che ci rendono unici nel panorama educativo." }</Typography>
              </Stack>
              <ValuesContainer theme={ theme } >
                { valuesList.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <ValueCard key={ `value_${index}` } theme={ theme } $isMobile={ isMobile }>
                      <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "24px" }>
                        <Icon size={ 48 } color={ theme.customColors.backgroundPrimaryCta }/>
                        <Typography variant={ "h3" } fontSize={ 20 } fontWeight={ 700 }>{ value.title }</Typography>
                        <Typography fontSize={ 14 }>{ value.description }</Typography>
                      </Stack>
                    </ValueCard>
                  );
                }) }
              </ValuesContainer>
            </Stack>
          </Box>
        </Container>
      </Box>

      { /*Altro*/ }
      <Container $isMobile={ isMobile }>
        <Box sx={ {
          paddingY: "95px",
          textAlign: "center"
        } }>
          <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "48px" }>
            <Button onClick={ () => navigate(homeLink.path) } variant={ "contained" } endIcon={ <ArrowRight/> }>Vai alla pagina { homeLink.label }</Button>
            <Typography fontSize={ 12 }>Accedi ai contenuti personalizzati dove trovi anche i Programmi di Academy4Future se disponibili per te</Typography>
          </Stack>
        </Box>
      </Container>

      <Modal
        description={ getError(learningObjectError)?.[0]?.message ?? "" }
        open={ learningObjectIsError && showModal }
        title={ getError(learningObjectError)?.[0]?.name ?? "" }
        onClose={ ()=> {
          setShowModal(false);
          navigate(-1);
        } }
      />
    </>
  );
}
